import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { clone } from "three/addons/utils/SkeletonUtils.js";
export class Assets {
  constructor() {
    this.loader = new GLTFLoader();
    this.cache = new Map();
    this.failures = new Map();
  }
  async load(name) {
    if (!this.cache.has(name)) {
      const task = (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
          // Development-only, one-shot failure injection for reproducible retry QA.
          if (
            import.meta.env?.DEV &&
            !this.injectedFailure &&
            new URLSearchParams(location.search).get("failAsset") === name
          ) {
            this.injectedFailure = true;
            await Promise.reject(new Error(`${name}: simulated load failure`));
          }
          const base = name.startsWith("free/") ? "/planet/" : "/island/";
          const file = name.replace(/^free\//, "");
          const response = await fetch(`${base}${file}.glb`, {
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`${name}: ${response.status}`);
          const data = await response.arrayBuffer();
          const gltf = await this.loader.parseAsync(data, base);
          // Grade each shared material once; preserve the original CC0 texture files.
          const graded = new Set();
          gltf.scene.traverse(child => {
            if (!child.isMesh || name === "girl") return;
            for (const material of Array.isArray(child.material) ? child.material : [child.material]) {
              if (graded.has(material)) continue;
              graded.add(material);
              material.roughness = 0.95;
              material.metalness = 0;
              material.onBeforeCompile = shader => {
                shader.fragmentShader = shader.fragmentShader.replace("#include <color_fragment>", `#include <color_fragment>
                  float softLuma = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
                  diffuseColor.rgb = mix(vec3(softLuma), diffuseColor.rgb, 0.72) * vec3(1.0, 0.98, 0.92);
                `);
              };
              material.customProgramCacheKey = () => "mimico-warm-palette-v1";
            }
          });
          this.failures.delete(name);
          return gltf;
        } catch (error) {
          this.cache.delete(name);
          this.failures.set(name, error);
          throw error;
        } finally {
          clearTimeout(timeout);
        }
      })();
      this.cache.set(name, task);
    }
    return this.cache.get(name);
  }
  async instance(name) {
    const gltf = await this.load(name);
    const object = name === "girl" ? clone(gltf.scene) : gltf.scene.clone(true);
    object.animations = gltf.animations;
    object.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    return object;
  }
}
